import { useMutation } from '@apollo/client';
import { useLingui } from '@lingui/react';
import {
  Form,
  Select,
  DatePickerProps,
  DatePicker,
  ConfigProvider,
} from 'antd';
import type { FormInstance } from 'antd/es/form';
import TextArea from 'antd/es/input/TextArea';
import dayjs from 'dayjs';
import React, { useState, useEffect } from 'react';
import { StoredHolidays } from '~/gql/holidays/holidays';

import { StoredProjectShortInfo } from '~/gql/project/project';
import { GQL_CREATE_OR_UPDATE_TIME } from '~/gql/time/time';
import { authClient } from '~/helpers/apollo';
import { useAuth } from '~/helpers/store/auth';
import { isDateHolidays } from '~/pages/components/common/utils-table/utils';

import DurationItemSmall from '~/pages/components/ui/DurationItemSmall';
import ModalCustom from '~/pages/components/ui/ModalCustom';
import { TDateTimes } from '~/pages/types/types';

type IProps = {
  projects: StoredProjectShortInfo[];
  projectId: number;
  taskId: number;
  item: TDateTimes;
  holydays: StoredHolidays[];
  title: string;
  isOpen: boolean;
  confirm: () => void;
  close: () => void;
};

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};
const dateFormat = 'DD-MM-YYYY';

function EditTime({
  title,
  isOpen,
  confirm,
  holydays,
  close,
  taskId,
  item,
  projectId,
  projects,
}: IProps) {
  const { i18n } = useLingui();
  const { payload } = useAuth();

  const formRef = React.useRef<FormInstance>(null);
  const [selectedProject, setSelectedProject] = useState<number | null>(
    projectId
  );
  const [selectedTask, setSelectedTask] = useState<number | null>(taskId);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    new Date(item.date)
  );
  const [comment, setComment] = useState<string>(item.timeComment ?? '');
  const [duration, setDuration] = useState<number>(item.time ?? 0);
  const [messageAlert, setMessageAlert] = useState<string>('');

  const [createOrUpdateTime] = useMutation(GQL_CREATE_OR_UPDATE_TIME, {
    client: authClient,
  });

  useEffect(() => {
    formRef.current?.resetFields();

    setSelectedProject(projectId);
    setSelectedTask(taskId);
    setSelectedDate(new Date(item.date));
    setComment(item.timeComment ?? '');
    setDuration(item.time ?? 0);
  }, [item]);

  useEffect(() => {
    if (selectedDate) {
      const isHolidays = isDateHolidays(selectedDate, holydays);
      const jourSemaine = new Date(selectedDate).getDay();

      if (jourSemaine === 0 || jourSemaine === 6 || isHolidays) {
        setMessageAlert(
          jourSemaine === 0 || jourSemaine === 6
            ? i18n._('common.day.alert.we')
            : i18n._('common.day.alert.free')
        );
      } else {
        setMessageAlert('');
      }
    } else {
      setMessageAlert('');
    }
  }, [selectedDate]);

  async function submit() {
    if (
      !payload?.sub ||
      !selectedProject ||
      !selectedTask ||
      !selectedDate ||
      !duration
    )
      return;

    const { data: _data } = await createOrUpdateTime({
      variables: {
        taskId: selectedTask,
        ...(item.timeId && { id: item.timeId }),
        date: selectedDate,
        duration: duration,
        userId: payload.sub,
        comment: comment,
      },
    });

    if (_data?.createOrUpdateTime) {
      confirm();
    } else {
      close();
    }
  }

  const onChangeDate: DatePickerProps['onChange'] = (date) => {
    const d = date.toDate().setHours(8, 0, 0, 0);
    setSelectedDate(new Date(d));
  };

  return (
    <ModalCustom
      confirmDisabled={!selectedProject || !selectedTask || !selectedDate}
      title={title}
      isOpen={isOpen}
      confirm={submit}
      close={() => {
        close();
      }}
    >
      <Form
        {...layout}
        ref={formRef}
        name="time-edit"
        initialValues={{
          projectName: projectId,
          taskName: taskId,
          date: dayjs(item.date as unknown as string),
          comment: item.timeComment ?? '',
        }}
        style={{ maxWidth: 600 }}
      >
        <Form.Item
          name="projectName"
          label={i18n._('form.project-name')}
          rules={[
            {
              required: true,
              message: i18n._('form.project-name.placeholder'),
            },
          ]}
        >
          <Select
            placeholder={i18n._('form.project.placeholder')}
            value={selectedProject}
            showSearch
            disabled
            onChange={setSelectedProject}
            style={{ width: '100%', padding: '2px' }}
            optionFilterProp="label"
            options={projects.map((p) => ({
              value: p.id,
              label: p.name,
            }))}
          />
        </Form.Item>

        <Form.Item
          name="taskName"
          label={i18n._('form.task.placeholder')}
          rules={[
            {
              required: true,
              message: i18n._('form.task-name.placeholder'),
            },
          ]}
        >
          <ConfigProvider renderEmpty={() => 'Choisissez le projet'}>
            <Select
              placeholder={i18n._('form.task-name.placeholder')}
              value={selectedTask}
              showSearch
              onChange={setSelectedTask}
              style={{ width: '100%', padding: '2px' }}
              optionFilterProp="label"
              options={projects
                .find((p) => p.id === projectId)
                ?.tasks.map((p) => ({
                  value: p.id,
                  label: p.name,
                }))}
            />
          </ConfigProvider>
        </Form.Item>
        <Form.Item
          name="date"
          style={{ width: '100%' }}
          label={i18n._('form.date')}
          validateStatus={messageAlert ? 'warning' : ''}
          help={messageAlert ? messageAlert : ''}
          rules={[
            {
              required: true,
              message: i18n._('form.project-name.placeholder'),
            },
          ]}
        >
          <DatePicker
            style={{ width: '100%' }}
            placeholder={i18n._('form.date.placeholder')}
            format={dateFormat}
            onChange={onChangeDate}
          />
        </Form.Item>
        <Form.Item
          name="duration"
          style={{ width: '100%' }}
          label={i18n._('common.times.hours')}
          rules={[
            {
              required: true,
              message: i18n._('form.project-name.placeholder'),
            },
          ]}
        >
          <DurationItemSmall
            $align="flex-start"
            $allowCorrection
            duration={duration}
            onChange={setDuration}
          />
        </Form.Item>
        <Form.Item name="comment" label={i18n._('form.time.comment')}>
          <TextArea
            rows={4}
            placeholder={i18n._('form.time.comment.placeholder')}
            onChange={(e) => setComment(e.target.value)}
          />
        </Form.Item>
      </Form>
    </ModalCustom>
  );
}

export default EditTime;
